import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { Tag } from '../tag';

interface TagsElasticContainerProps {
  // A record renders `key=value` pairs, for resource tags. An array renders each
  // entry as it stands and keeps duplicates, which a record cannot: two distinct
  // things can share a label, and collapsing them hides one.
  tags: Record<string, string> | string[];
  expandable?: boolean;
}

// The width to lay tags out against. This element renders only what fits, so its own content is
// what gives it a width - inside an ancestor that shrinks to fit, that is nothing, and a zero
// measurement hides every tag behind the overflow count. The nearest ancestor with a width answers
// for it instead.
const availableWidth = (element: HTMLElement): number => {
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    const { width } = node.getBoundingClientRect();
    if (width > 0) {
      return width;
    }
  }
  return 0;
};

const tagValues = (tags: Record<string, string> | string[]) =>
  Array.isArray(tags)
    ? tags
    : Object.entries(tags).map((entry) => entry.filter((x) => x).join('='));

export const TagsElasticContainer = ({
  tags,
  expandable,
}: TagsElasticContainerProps) => {
  if (!tagValues(tags || {}).length) {
    return null;
  }

  return <InnerTagsElasticContainer tags={tags} expandable={expandable} />;
};

const InnerTagsElasticContainer = ({
  tags,
  expandable,
}: TagsElasticContainerProps) => {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [shownTagsCount, setShownTagsCount] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const values = useMemo(() => tagValues(tags), [tags]);

  useEffect(() => {
    const observed: HTMLElement[] = [];
    const observer = new ResizeObserver(() => {
      if (ref.current) {
        setWidth(availableWidth(ref.current));
      }
    });

    if (ref.current) {
      // The parent is observed too: when this element has no width of its own, its size never
      // changes and its own observations would never fire again.
      for (const element of [ref.current, ref.current.parentElement]) {
        if (element) {
          observer.observe(element);
          observed.push(element);
        }
      }
      // Seed rather than wait for the observer's first callback: when that does not land the
      // measurement below never runs, and every tag stays hidden behind the overflow count.
      setWidth(availableWidth(ref.current));
    }

    return () => {
      for (const element of observed) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    if (width === 0 || !ref.current) {
      return;
    }

    const tagsWithoutPlaceholder = getTagNodes(ref, 0);
    if (!tagsWithoutPlaceholder.length) {
      return;
    }

    let firstTagY = tagsWithoutPlaceholder[0].getBoundingClientRect().y;
    if (
      tagsWithoutPlaceholder.every(
        (x) => x.getBoundingClientRect().y === firstTagY
      )
    ) {
      return setShownTagsCount(tagsWithoutPlaceholder.length);
    }

    const tagsWithPlaceholder = getTagNodes(ref, 1);
    if (!tagsWithPlaceholder.length) {
      return;
    }

    firstTagY = tagsWithPlaceholder[0].getBoundingClientRect().y;
    const tagsOnFirstRow =
      tagsWithPlaceholder.filter(
        (x) => x.getBoundingClientRect().y === firstTagY
      ).length - 1;

    setShownTagsCount(Math.max(tagsOnFirstRow, 1));
  }, [ref, width]);

  const hiddenTagsCount = values.length - shownTagsCount;
  const hasOverflow = hiddenTagsCount > 0;
  const displayedValues =
    expandable && expanded ? values : values.slice(0, shownTagsCount);

  return (
    <Box className='relative' ref={ref}>
      <TagsContainerRenderer withPlaceholder={false} values={values} />
      <TagsContainerRenderer withPlaceholder={true} values={values} />
      <Stack
        direction='row'
        sx={{
          gap: '8px',
          alignItems: 'center',
          flexWrap: expandable && expanded ? 'wrap' : undefined,
        }}
      >
        {displayedValues.map((value, index) => (
          <Tag key={`${value}-${index}`} variant='secondary' content={value} />
        ))}
        {hasOverflow && !expandable && (
          <Typography variant='body2'>{`+${hiddenTagsCount}`}</Typography>
        )}
        {hasOverflow && expandable && (
          <ExpandCollapseButton
            expanded={expanded}
            hiddenCount={hiddenTagsCount}
            onToggle={() => setExpanded((prev) => !prev)}
          />
        )}
      </Stack>
    </Box>
  );
};

const ExpandCollapseButton = ({
  expanded,
  hiddenCount,
  onToggle,
}: {
  expanded: boolean;
  hiddenCount: number;
  onToggle: () => void;
}) => (
  <ButtonBase
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    sx={{
      borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
      padding: '3px 8px',
      border: '1px solid var(--mui-palette-divider)',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-primary-dark)',
        borderColor: 'var(--mui-palette-primary-dark)',
      },
    }}
  >
    <Typography variant='body2'>
      {expanded ? 'Show less' : `+${hiddenCount}`}
    </Typography>
  </ButtonBase>
);

const getTagNodes = (
  rootRef: RefObject<HTMLDivElement | null>,
  index: number
) => {
  if (!rootRef.current?.childNodes) {
    return [];
  }

  const tagsContainer = rootRef.current.childNodes.item(index) as HTMLElement;
  if (!tagsContainer?.childNodes.length) {
    return [];
  }

  return Array.from(tagsContainer.childNodes) as HTMLElement[];
};

const TagsContainerRenderer = ({
  values,
  withPlaceholder,
}: {
  withPlaceholder: boolean;
  values: string[];
}) => (
  <Stack
    direction='row'
    className='absolute top-0 left-0 right-0 invisible'
    sx={{
      gap: '8px',
      flexWrap: 'wrap',
    }}
  >
    {withPlaceholder && (
      <ExpandCollapseButton
        expanded={false}
        hiddenCount={99}
        onToggle={() => {}}
      />
    )}
    {values.map((value, index) => (
      <Tag key={`${value}-${index}`} variant='secondary' content={value} />
    ))}
  </Stack>
);
